const portalHost = `${process.env.REACT_APP_PET_PARENT_PORTAL_HOST_URL}`;

const HANDOFF_PARAM = 'handoff';
const FROM_PORTAL_KEY = 'olb.fromPortal';
const ADDONS_BOOKING_PARAM = 'booking_id';
const MODE_PARAM = 'mode';
const ADDONS_MODE = 'addons';

/**
 * Reads the handoff code and strips it from the address bar in one go. The code
 * is single-use, so leaving it in the URL only makes a reload look like a
 * tampered session.
 */
export const takeHandoffCode = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get(HANDOFF_PARAM);

    if (!code) {
        return null;
    }

    params.delete(HANDOFF_PARAM);
    const search = params.toString();
    window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${search ? `?${search}` : ''}`
    );

    return code;
};

export interface AddonsModeEntry {
    bookingId: number;
    // True when the tab was reloaded: the store (and the session token) is gone by then.
    restored: boolean;
}

/**
 * The portal opens the Add Add-Ons flow with `?mode=addons&booking_id=<id>`. Both params stay in
 * the address bar (only the handoff code leaves it), so a reload lands on the add-ons flow
 * ("session expired", back to the booking) and any other URL of the tab is the public online
 * booking. Nothing is stored: an abandoned add-ons flow never follows the tab around.
 */
export const takeAddonsModeEntry = (hasHandoffCode: boolean): AddonsModeEntry | null => {
    const params = new URLSearchParams(window.location.search);
    const bookingId = Number(params.get(ADDONS_BOOKING_PARAM));

    if (params.get(MODE_PARAM) !== ADDONS_MODE || !Number.isInteger(bookingId) || bookingId <= 0) {
        return null;
    }

    return { bookingId, restored: !hasHandoffCode };
};

export const markArrivedFromPortal = (): void => {
    try {
        sessionStorage.setItem(FROM_PORTAL_KEY, '1');
    } catch {
        // Private mode and friends: the flow still works, only the way back is lost.
    }
};

export const arrivedFromPortal = (): boolean => {
    try {
        return sessionStorage.getItem(FROM_PORTAL_KEY) === '1';
    } catch {
        return false;
    }
};

/** Leaves for the portal: its bookings list, or the detail of `bookingId`. */
export const goToPetParentPortal = (marketplaceSlug: string, bookingId?: number): void => {
    try {
        sessionStorage.removeItem(FROM_PORTAL_KEY);
    } catch {
        // Ignored, the portal is the destination either way.
    }

    const path = `${marketplaceSlug}/bookings${bookingId ? `/${bookingId}` : ''}`;

    window.location.assign(`${portalHost.replace(/\/+$/, '')}/${path}`);
};
